import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { BUSINESS_ID } from '../utils/seo';
/**
 * Replaces the former Ceramic Coating page.
 *
 * Mike's Pro Detail applies a sprayed-on hybrid ceramic product, not a
 * professional multi-year ceramic coating. This page describes only what is
 * actually offered and deliberately avoids the words "ceramic coating",
 * "professional ceramic coating", and "permanent", along with any multi-year
 * durability claim.
 */
export function HybridCeramicSprayPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Hybrid Ceramic Spray Protection',
    serviceType: 'Hybrid Ceramic Spray Paint Protection',
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: ['Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks', 'Ventura County'],
    description: 'Sprayed-on hybrid ceramic protection that adds gloss and helps paint shed water and dirt, applied on-site anywhere in Ventura County.'
  };
  const faqs = [{
    question: 'What is hybrid ceramic spray?',
    answer: 'Hybrid ceramic spray is a sprayed-on paint protection product that combines traditional wax or sealant technology with ceramic (silica-based) ingredients. It adds gloss and helps your paint shed water and dirt, and it is applied by hand as part of a detail.'
  }, {
    question: 'Is hybrid ceramic spray the same as a ceramic coating?',
    answer: 'No. A professional ceramic coating is a separate, multi-step service that bonds a durable layer to your clear coat and typically requires paint correction first. Hybrid ceramic spray is a sprayed-on protectant applied during a detail. Mike\u2019s Pro Detail offers hybrid ceramic spray, not professional ceramic coating.'
  }, {
    question: 'How long does hybrid ceramic spray last?',
    answer: 'Longevity depends on the product, the weather, and how often the vehicle is washed and where it is parked. It outlasts a traditional carnauba wax, but it is not a permanent or multi-year coating. Call (818) 381-9905 and we will give you a realistic expectation for your vehicle.'
  }, {
    question: 'Is hybrid ceramic spray included in any package?',
    answer: 'Yes. Hybrid ceramic protection is included in our Wash & Wax package and our Full Detail. It can also be added to other services — call (818) 381-9905 for a quote.'
  }, {
    question: 'Can you apply it at my home or office?',
    answer: 'Yes. Like every service we offer, hybrid ceramic spray is applied on-site at your home or office anywhere in Ventura County. We bring our own water and detailing equipment and require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Hybrid Ceramic Spray',
    item: 'https://www.fulldetailpro.com/services/hybrid-ceramic-spray'
  }];
  return <ServicePageLayout title="Hybrid Ceramic Spray Protection in Ventura County" subtitle="Added gloss and water-beading protection for your paint, sprayed on by hand as part of your mobile detail." metaDescription="Hybrid ceramic spray protection in Ventura County — Camarillo, Oxnard, Ventura & Thousand Oaks. Added gloss and water beading, applied at your door. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-1086" data-mp="1135kx99zbu0i">
      <p data-id="element-1087" data-mp="i5cs4zt2l0gu">
        Hybrid ceramic spray is the paint protection we apply at the end of a{' '}
        <Link to="/services/exterior-detailing" data-id="element-1088" data-mp="3ggcagkruj5o">mobile exterior detail</Link>.
        It is a sprayed-on product that blends traditional sealant technology
        with silica-based ceramic ingredients, and it leaves your paint glossier
        and better able to shed water, dust, and road grime. Mike's Pro Detail
        applies it on-site at your home or office throughout Ventura County —
        Camarillo, Oxnard, Ventura, Thousand Oaks, and the surrounding
        communities.
      </p>

      <h2 data-id="element-1089" data-mp="3ij5cwj3eb80c">Hybrid Ceramic Spray vs. Ceramic Coating</h2>
      <p data-id="element-1090" data-mp="3ilrjogigpjhi">
        These two are often confused, so it is worth being clear about what you
        are getting. A professional ceramic coating is a separate, highly
        technical service: the paint has to be corrected first, the coating
        bonds to the clear coat, and it needs a controlled environment and a
        long cure time.{' '}
        <strong data-id="element-1091" data-mp="2zrw2m7lswf7p">
          Mike's Pro Detail offers hybrid ceramic spray, not professional ceramic
          coating.
        </strong>{' '}
        Hybrid ceramic spray is applied by hand during your detail, costs a
        fraction as much, and delivers real gloss and water beading without the
        multi-day process. We would rather tell you exactly what you are buying
        than sell you a term that does not match the work.
      </p>

      <h2 data-id="element-1092" data-mp="oyrseet7g2ib">What Hybrid Ceramic Spray Does for Your Paint</h2>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-1093" data-mp="26xg00xzydv3n">
        <li className="flex items-start" data-id="element-1094" data-mp="2bxlxiuswmbtn">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1095" data-mp="2bwkdw1xtoh0b" />
          <span data-id="element-1096" data-mp="2gnskkq7rpo9s">
            <strong data-id="element-1097" data-mp="2go2eupzuihla">Water Beading:</strong> Rain and wash water bead up and roll
            off instead of sheeting across the panel, carrying loose dirt with
            them.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1098" data-mp="1o0om7436k86z">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1099" data-mp="1o1pwh6cdgqnn" />
          <span data-id="element-1100" data-mp="1sz9od91zt1y2">
            <strong data-id="element-1101" data-mp="1t0u781ey2wg3">Added Gloss:</strong> The finish looks deeper and more
            reflective, especially on darker paint, immediately after
            application.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1102" data-mp="220pkqcvz33ja">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1103" data-mp="38g00fofg8wn4" />
          <span data-id="element-1104" data-mp="38hum54yjoss9">
            <strong data-id="element-1105" data-mp="3debao09np95v">Easier Washing:</strong> Dust, pollen, and light road grime
            release more easily, so routine washes take less effort and are
            gentler on the paint.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1106" data-mp="19bi3j925e30s">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1107" data-mp="19agt96syhkk4" />
          <span data-id="element-1108" data-mp="1n9gh4c8n9vf8">
            <strong data-id="element-1109" data-mp="1n9zpp2cen7gl">A Barrier Against the Elements:</strong> Adds a sacrificial
            layer between your clear coat and the Southern California sun,
            coastal salt air, and everyday contamination.
          </span>
        </li>
      </ul>

      <h2 data-id="element-1110" data-mp="pn0w50h0efmx">How We Apply It</h2>
      <p data-id="element-1111" data-mp="pnsn1xznvp6v">
        Protection only performs as well as the surface underneath it, so the
        paint is washed and decontaminated before anything is applied. We hand
        wash the vehicle, clay bar the paint to lift bonded contamination, dry
        it thoroughly, then spray and buff the hybrid ceramic product panel by
        panel. Because we are fully mobile, all of this happens in your
        driveway or parking space.
      </p>

      <h2 data-id="element-1112" data-mp="6uf6ixwp3las">Where Hybrid Ceramic Spray Is Included</h2>
      <p data-id="element-1113" data-mp="2iik08e66b2kb">
        Hybrid ceramic protection is already part of our{' '}
        <Link to="/full-detail" data-id="element-1114" data-mp="2nav1bkcngueg">Full Detail</Link> and our Wash
        &amp; Wax package, and it can be added to most other services. We also
        offer a hybrid ceramic glass treatment and a longer-duration hybrid
        ceramic treatment — call{' '}
        <a href="tel:8183819905" data-id="element-1115" data-mp="2dks232exqk7o">(818) 381-9905</a> and we will recommend the
        right option for your vehicle and quote it honestly.
      </p>

      <h2 data-id="element-1116" data-mp="369pg2tc45uum">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-1117" data-mp="368nvny97jw08">
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1118" data-mp="2s8d40t02qde0">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1119" data-mp="1ltcfh8ddoxhg">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1120" data-mp="28grd2j8395gz">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/mobile-detailing-camarillo" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1121" data-mp="2w7f0kcuhm3on">
          Mobile Detailing in Camarillo &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}